import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import DatePicker from '../../../componants/datePicker/DatePicker'
import './groupDetail.css'
import './addExpense.css'

// Wired to POST /splitwise/groups/<group_id>/expenses/. Members with status
// "pending" have no user account behind their SplitMember row yet, so they
// can't really carry a share long-term — that exclusion is temporarily
// switched off per the user's ask ("abhi k liye dikha do, condition baad
// me apply karenge"). Flip this back to true to re-enable it.
const ONLY_JOINED_MEMBERS = false

const AVATAR_COLORS = ['#FDE68A', '#FBCFE8', '#BFDBFE', '#FED7AA', '#FBCFE8']

const SPLIT_METHODS = ['Equal', 'Custom', 'Percentage']

const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#1D2A4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const CheckIcon = () => (
    <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
        <path d="M11.5 3.5 5.5 10.5 2.5 7.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const Avatar = ({ member, index }) => (
    member.image ? (
        <img src={member.image} alt="" className='expenseAvatar expenseAvatar--photo' />
    ) : (
        <span className='expenseAvatar' style={{ background: AVATAR_COLORS[index % AVATAR_COLORS.length] }}>
            {member.name.charAt(0)}
        </span>
    )
)

const formatMoney = (value) => Math.round(value).toLocaleString('en-IN')

const todayIso = () => new Date().toISOString().slice(0, 10)

const AddExpense = ({ groupId: groupIdProp, onBack }) => {
    const groupId = Number(groupIdProp) || null
    const currentUserId = useSelector((state) => state.auth.user?.id)

    const [group, setGroup] = useState(null)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)

    const [expenseTitle, setExpenseTitle] = useState('')
    const [amount, setAmount] = useState('')
    const [paidById, setPaidById] = useState(null)
    const [date, setDate] = useState(todayIso())
    const [note, setNote] = useState('')
    const [involvedIds, setInvolvedIds] = useState([])
    const [splitMethod, setSplitMethod] = useState('Equal')
    const [customAmounts, setCustomAmounts] = useState({})
    const [percentages, setPercentages] = useState({})

    useEffect(() => {
        if (!groupId) { setLoading(false); return }
        setLoading(true)
        service
            .get(endpoints.splitwise.groupDetail.replace('{group_id}', groupId), {}, { silent: true })
            .then(({ data }) => {
                const fetched = data.data
                setGroup(fetched)
                const allMembers = fetched?.members || []
                const joined = ONLY_JOINED_MEMBERS ? allMembers.filter((m) => m.status === 'joined') : allMembers
                setInvolvedIds(joined.map((m) => m.id))
                const me = joined.find((m) => m.user === currentUserId)
                setPaidById((me || joined[0])?.id ?? null)
            })
            .catch(() => setGroup(null))
            .finally(() => setLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId])

    const members = useMemo(() => {
        const all = group?.members || []
        return ONLY_JOINED_MEMBERS ? all.filter((m) => m.status === 'joined') : all
    }, [group])

    const amountNum = Number(String(amount).replace(/[^0-9.]/g, '')) || 0
    const involvedMembers = members.filter((m) => involvedIds.includes(m.id))
    const allSelected = involvedIds.length === members.length && members.length > 0

    const toggleMember = (id) => {
        setInvolvedIds((prev) => (prev.includes(id) ? prev.filter((mid) => mid !== id) : [...prev, id]))
    }

    const toggleSelectAll = () => {
        setInvolvedIds(allSelected ? [] : members.map((m) => m.id))
    }

    const equalShare = involvedMembers.length ? amountNum / involvedMembers.length : 0

    const customSum = involvedMembers.reduce((sum, m) => sum + (Number(String(customAmounts[m.id]).replace(/[^0-9.]/g, '')) || 0), 0)
    const customRemaining = amountNum - customSum
    const customAllocatedPct = amountNum ? Math.min(100, Math.round((customSum / amountNum) * 100)) : 0
    const customComplete = Math.abs(customRemaining) < 0.5 && amountNum > 0

    const percentSum = involvedMembers.reduce((sum, m) => sum + (Number(String(percentages[m.id]).replace(/[^0-9.]/g, '')) || 0), 0)
    const percentComplete = Math.abs(percentSum - 100) < 0.5

    const paidByMember = members.find((m) => m.id === paidById)

    const paidByShare = useMemo(() => {
        if (!paidByMember) return 0
        if (splitMethod === 'Equal') return equalShare
        if (splitMethod === 'Custom') return Number(String(customAmounts[paidByMember.id]).replace(/[^0-9.]/g, '')) || 0
        const pct = Number(String(percentages[paidByMember.id]).replace(/[^0-9.]/g, '')) || 0
        return (amountNum * pct) / 100
    }, [paidByMember, splitMethod, equalShare, customAmounts, percentages, amountNum])

    const othersOweYou = amountNum - paidByShare

    const canSave = Boolean(
        expenseTitle.trim()
        && amountNum > 0
        && paidById
        && date
        && involvedMembers.length > 0
        && (splitMethod === 'Equal' ? true : splitMethod === 'Custom' ? customComplete : percentComplete)
    )

    const buildShares = () => {
        if (splitMethod === 'Custom') {
            return involvedMembers.map((m) => ({
                member_id: m.id,
                share_amount: String(Number(String(customAmounts[m.id]).replace(/[^0-9.]/g, '')) || 0),
            }))
        }
        if (splitMethod === 'Percentage') {
            return involvedMembers.map((m) => ({
                member_id: m.id,
                percentage: String(Number(String(percentages[m.id]).replace(/[^0-9.]/g, '')) || 0),
            }))
        }
        return involvedMembers.map((m) => ({ member_id: m.id }))
    }

    const handleSave = () => {
        if (!canSave || submitting || !groupId) return
        setSubmitting(true)
        service
            .post(endpoints.splitwise.groupExpenses.replace('{group_id}', groupId), {
                title: expenseTitle.trim(),
                amount: amountNum.toFixed(2),
                paid_by: paidById,
                expense_date: date,
                split_type: splitMethod.toLowerCase(),
                shares: buildShares(),
                note: note.trim(),
            })
            .then(() => onBack())
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setSubmitting(false))
    }

    if (loading) {
        return <p className='bsFareNote'>Loading group...</p>
    }

    return (
        <div>
            <div className='addExpensePage__backRow'>
                <button type='button' className='groupDetailBack' onClick={onBack}>
                    <BackArrow />
                </button>
                <div>
                    <div className='addExpensePage__backTitle'>Add New Expense</div>
                    <div className='addExpensePage__backSubtitle'>Add an expense and split it with your group</div>
                </div>
            </div>

            <div className='addExpensePage__card'>
                <div className='addExpensePage__row'>
                    <div className='addExpensePage__field addExpensePage__field--grow'>
                        <label>Expense Title</label>
                        <input type='text' placeholder='e.g., Dinner at Thalassa' value={expenseTitle} onChange={(e) => setExpenseTitle(e.target.value)} />
                    </div>
                    <div className='addExpensePage__field'>
                        <label>Amount</label>
                        <div className='addExpensePage__amountInput'>
                            <span>₹</span>
                            <input type='text' value={amount} onChange={(e) => setAmount(e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className='addExpensePage__row'>
                    <div className='addExpensePage__field'>
                        <label>Paid By</label>
                        <select value={paidById ?? ''} onChange={(e) => setPaidById(Number(e.target.value))}>
                            {members.map((member) => (
                                <option key={member.id} value={member.id}>{member.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className='addExpensePage__field'>
                        <label>Date</label>
                        <DatePicker value={date} onChange={setDate} />
                    </div>
                </div>

                <div className='addExpensePage__involvedHead'>
                    <label>Who was involved?</label>
                    <span className='addExpensePage__selectAll' onClick={toggleSelectAll}>
                        {allSelected ? 'Deselect All' : `Select All (${members.length})`}
                    </span>
                </div>
                <div className='expenseChips'>
                    {members.map((member, index) => {
                        const checked = involvedIds.includes(member.id)
                        return (
                            <span
                                className={`expenseChip ${checked ? 'checked' : ''}`}
                                key={member.id}
                                onClick={() => toggleMember(member.id)}
                            >
                                <Avatar member={member} index={index} />
                                {checked && <CheckIcon />}
                                {member.name}
                            </span>
                        )
                    })}
                </div>

                <label className='addExpensePage__sectionLabel'>Split Method</label>
                <div className='addExpensePage__splitMethod'>
                    {SPLIT_METHODS.map((method) => (
                        <div
                            key={method}
                            className={`addExpensePage__segment ${splitMethod === method ? 'active' : ''}`}
                            onClick={() => setSplitMethod(method)}
                        >
                            {method}
                        </div>
                    ))}
                </div>

                {splitMethod === 'Equal' && (
                    <div className='expenseSplitBox'>
                        <div className='expenseSplitBox__note'>
                            Total Expense ₹{formatMoney(amountNum)} · {involvedMembers.length} people · ₹{formatMoney(equalShare)} / person
                        </div>
                        {involvedMembers.map((member) => {
                            const index = members.findIndex((m) => m.id === member.id)
                            return (
                                <div className='expenseSplitRow' key={member.id}>
                                    <span className='expenseSplitRow__user'><Avatar member={member} index={index} />{member.name}</span>
                                    <span className='expenseSplitRow__amount'>₹{formatMoney(equalShare)}</span>
                                </div>
                            )
                        })}
                    </div>
                )}

                {splitMethod === 'Custom' && (
                    <div className='expenseSplitBox'>
                        <div className='expenseSplitBox__head'>
                            <span>Split by custom amounts</span>
                            {customComplete ? (
                                <span className='expenseSplitBox__status expenseSplitBox__status--ok'>✓ Fully allocated</span>
                            ) : (
                                <span className='expenseSplitBox__status expenseSplitBox__status--warn'>
                                    ₹{formatMoney(Math.abs(customRemaining))} {customRemaining >= 0 ? 'remaining' : 'over'}
                                </span>
                            )}
                        </div>
                        {involvedMembers.map((member) => {
                            const index = members.findIndex((m) => m.id === member.id)
                            return (
                                <div className='expenseSplitRow' key={member.id}>
                                    <span className='expenseSplitRow__user'><Avatar member={member} index={index} />{member.name}</span>
                                    <div className='expenseSplitRow__input'>
                                        <span>₹</span>
                                        <input
                                            type='text'
                                            value={customAmounts[member.id] || ''}
                                            onChange={(e) => setCustomAmounts((prev) => ({ ...prev, [member.id]: e.target.value }))}
                                        />
                                    </div>
                                </div>
                            )
                        })}
                        <div className='expenseSplitBox__progressRow'>
                            <span>Allocated ₹{formatMoney(customSum)} of ₹{formatMoney(amountNum)}</span>
                        </div>
                        <div className='expenseSplitBox__progressTrack'>
                            <div
                                className={`expenseSplitBox__progressFill ${customComplete ? 'complete' : ''}`}
                                style={{ width: `${customAllocatedPct}%` }}
                            />
                        </div>
                        {!customComplete && (
                            <div className='expenseSplitBox__hint'>* Total must equal ₹{formatMoney(amountNum)} to save</div>
                        )}
                    </div>
                )}

                {splitMethod === 'Percentage' && (
                    <div className='expenseSplitBox'>
                        <div className='expenseSplitBox__head'>
                            <span>Split by percentages</span>
                            {percentComplete ? (
                                <span className='expenseSplitBox__status expenseSplitBox__status--ok'>100% allocated ✓</span>
                            ) : (
                                <span className='expenseSplitBox__status expenseSplitBox__status--warn'>{percentSum}% allocated</span>
                            )}
                        </div>
                        {involvedMembers.map((member) => {
                            const index = members.findIndex((m) => m.id === member.id)
                            const pct = Number(String(percentages[member.id]).replace(/[^0-9.]/g, '')) || 0
                            return (
                                <div className='expenseSplitRow' key={member.id}>
                                    <span className='expenseSplitRow__user'><Avatar member={member} index={index} />{member.name}</span>
                                    <span className='expenseSplitRow__amount expenseSplitRow__amount--muted'>₹{formatMoney((amountNum * pct) / 100)}</span>
                                    <div className='expenseSplitRow__input expenseSplitRow__input--pct'>
                                        <input
                                            type='text'
                                            value={percentages[member.id] || ''}
                                            onChange={(e) => setPercentages((prev) => ({ ...prev, [member.id]: e.target.value }))}
                                        />
                                        <span>%</span>
                                    </div>
                                </div>
                            )
                        })}
                        <div className='expenseSplitBox__progressTrack' style={{ marginTop: 14 }}>
                            <div
                                className={`expenseSplitBox__progressFill ${percentComplete ? 'complete' : ''}`}
                                style={{ width: `${Math.min(100, percentSum)}%` }}
                            />
                        </div>
                        <div className='expenseSplitBox__progressRow'>
                            <span>{percentComplete ? 'Fully allocated' : `${percentSum}% allocated`}</span>
                        </div>
                        {!percentComplete && (
                            <div className='expenseSplitBox__hint'>* Total must equal 100% to save</div>
                        )}
                    </div>
                )}

                <div className='addExpensePage__field' style={{ marginBottom: 20 }}>
                    <label>Note <span style={{ fontWeight: 500, color: '#9CA3AF' }}>(optional)</span></label>
                    <input type='text' placeholder='e.g., Table for 5 at the beachside cafe' value={note} onChange={(e) => setNote(e.target.value)} />
                </div>

                <div className='expenseSummary'>
                    <div className='expenseSummary__head'>
                        <span>SUMMARY</span>
                        <span>Paid by: {paidByMember?.name || '—'}</span>
                    </div>
                    <div className='expenseSummary__row'>
                        <span>Your share:</span>
                        <span>₹{formatMoney(paidByShare)}</span>
                    </div>
                    <div className='expenseSummary__row expenseSummary__row--owe'>
                        <span>Others owe you:</span>
                        <span>₹{formatMoney(othersOweYou)}</span>
                    </div>
                </div>

                <div className='addExpensePage__actions'>
                    <button type='button' className='addExpensePage__cancelBtn' onClick={onBack}>Cancel</button>
                    <button type='button' className='addExpensePage__addBtn' disabled={!canSave || submitting} onClick={handleSave}>
                        {submitting ? 'Saving...' : 'Save Expense'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default AddExpense
